import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { AudioAssetDto, AudioVersionDto, ChapterResult, Discrepancy, Pickup, PickupSnapshot, Project } from "../api/types";
import { settleSpan } from "../lib/pickupContext";
import { usePickups } from "../lib/pickups";
import { useBoundaryReproof } from "../lib/useBoundaryReproof";
import { applyReviewChanges, diffReviewState, fetchReviewUpdate, loadBase, mergeReviewState, patchReviewState, sameReviewState, saveBase } from "../lib/reviewState";
import { marksByFlag, useScriptNotes } from "../lib/scriptNotes";
import { AudioPlayer, type AudioHandle } from "../components/AudioPlayer";
import { ManuscriptPane, type LayoutResult, type SelectionFlagInput } from "../components/ManuscriptPane";
import { FindingRow, FindingsList, type ReviewedFinding } from "../components/FindingsList";
import { VersionSwitch } from "../components/VersionSwitch";
import { CurrentHit, FindBar, FindPanel, FindRail, type HitState, type PaneFind } from "../components/FindMode";
import { BoundaryCard } from "../components/BoundaryCard";
import { GeneralNotesPanel } from "../components/GeneralNotes";
import { ImportMarkersDialog } from "../components/ImportMarkersDialog";
import { NoIssuesDialog } from "../components/NoIssuesDialog";
import { RunPanel } from "../components/RunPanel";
import { ReportStrip } from "../components/ReportStrip";
import { ScriptReproofBanner } from "../components/ScriptReproofBanner";
import { ProjectMenu } from "../components/ProjectMenu";
import { ProjectHeader } from "../components/ProjectHeader";
import { StartProofingDialog, parkedFiles } from "../components/StartProofingDialog";
import { Icon } from "../components/icons";
import { toast } from "../components/toast";
import { discrepancyKey, headlineFor, loadState, saveState, signatureOf, categoryOf, CATEGORY_LABEL, FLAG_CATEGORIES, type ManualFlag, type MeaningClass, type FlagCategory, type ReportState } from "../lib/reportStore";
import { formatTime } from "../lib/format";
import { asDiscrepancy, clipOf, onWords, useFind, type FindHit } from "../lib/find";
import { useManuscriptPages } from "../lib/pageView";
import { shownFile, shownStem } from "../lib/fileNames";

const NO_MANUAL_FLAGS: ManualFlag[] = [];
const NO_HITS: FindHit[] = [];

// Find mode ("Find all of these in the manuscript"): the search, the hit you're
// on, and whether to play it once its file is open.
interface FindState {
  query: string;
  index: number;                 // the hit you're on; -1 until the search answers
  tick: number;                  // bumped on every move, so the panel shows it again
  play: boolean;                 // play its clip once its file is open
  from?: { chapterId: string; token: number };   // the words the search began from
}
const FIND_AUTOPLAY_KEY = "mb.find.autoplay";
const readFindAutoplay = () => { try { return localStorage.getItem(FIND_AUTOPLAY_KEY) !== "0"; } catch { return true; } };
const writeFindAutoplay = (on: boolean) => { try { localStorage.setItem(FIND_AUTOPLAY_KEY, on ? "1" : "0"); } catch { /* private window */ } };

const ALL_TYPES = FLAG_CATEGORIES;

/** A category string from the server, if it's one the report can show. */
function storedCategory(c: string | null | undefined): FlagCategory | undefined {
  return c && (FLAG_CATEGORIES as string[]).includes(c) ? (c as FlagCategory) : undefined;
}

/**
 * The Proofing page before anything is proofed.
 *
 * It used to say "start proofing from the Project Hub" and stop there - which
 * is a signpost, not a door, and the reader is already exactly where they want
 * the work to happen. If the files are sitting uploaded, they can start from
 * here; the page only sends them elsewhere when something is genuinely missing.
 */
function NothingProofedYet({ project, projectId }: { project: Project; projectId: string }) {
  const [picking, setPicking] = useState(false);
  const { data: assets } = useQuery({
    queryKey: ["audio", projectId],
    queryFn: () => api<{ assets: AudioAssetDto[] }>(`/api/projects/${projectId}/audio`).then((r) => r.assets),
  });
  const parked = parkedFiles(assets ?? []);

  if (project.proofing_active) {
    return (
      <div className="empty-state">
        <h3>Proofing is running</h3>
        <p>
          <span className="spinner" style={{ width: 12, height: 12, marginRight: 8 }} />
          The first chapter opens here as soon as it finishes.
        </p>
      </div>
    );
  }
  if (!project.has_manuscript) {
    return (
      <div className="empty-state">
        <h3>Nothing proofed yet</h3>
        <p>Proofing compares the narration against a manuscript, and this project has none yet.</p>
        <Link to={`/projects/${projectId}`} className="btn sm primary">Add a manuscript</Link>
      </div>
    );
  }
  if (parked.length === 0) {
    return (
      <div className="empty-state">
        <h3>Nothing proofed yet</h3>
        <p>{project.n_audio > 0
          ? "Every file here is already queued for proofing - the first chapter opens here as it finishes."
          : "Add the audio in the Project Hub and you can start proofing from here."}</p>
        {project.n_audio === 0 && (
          <Link to={`/projects/${projectId}`} className="btn sm primary">Add audio files</Link>
        )}
      </div>
    );
  }
  return (
    <div className="empty-state">
      {picking && (
        <StartProofingDialog projectId={projectId} files={parked} onClose={() => setPicking(false)} />
      )}
      <h3>Nothing proofed yet</h3>
      <p>
        {parked.length === 1
          ? "One file is uploaded and waiting - it was brought in without proofing. Start it here and it opens on this page as soon as it finishes."
          : `${parked.length} files are uploaded and waiting - they were brought in without proofing. Start them here and the first chapter opens on this page.`}
      </p>
      <button className="btn sm primary" onClick={() => setPicking(true)}>
        <Icon name="play" size={15} /> Start proofing…
      </button>
    </div>
  );
}



export function ReportViewer() {
  const { projectId, chapterId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: project } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => api<Project>(`/api/projects/${projectId}`),
    // While a background re-proof runs (it may have been launched from the
    // project hub, not here), poll so we notice the moment it clears — then the
    // effect below refetches the flags. Idle otherwise (no needless polling).
    refetchInterval: (q) => (q.state.data?.active_job?.job_type === "reproof" ? 2500 : false),
  });

  const proofed = useMemo(
    () => (project?.chapters || []).filter((c) => c.state === "proofed"),
    [project]
  );
  const currentId = chapterId || proofed[0]?.id;
  // Book layout for a take proofed before it was recorded — asked for only when
  // the reader switches to it. Stable per chapter, so the pane asks just once.
  const loadLayout = useCallback(
    () => api<LayoutResult>(`/api/modules/proofing/projects/${projectId}/chapters/${currentId}/layout`),
    [projectId, currentId]
  );

  // A boundary re-proof rewrites this chapter's flags on the server. The surface
  // that launched it (this report OR the project-hub take list) owns the live
  // job-watcher that would invalidate the cache — but that watcher dies if its
  // component unmounts (e.g. you change a boundary in the hub, then open the
  // report while the re-proof is still running). So the report could keep showing
  // the PRE-re-proof flags. Guard against that: when the active re-proof clears,
  // refetch the result here, regardless of who started it.
  const reproofActive = project?.active_job?.job_type === "reproof";
  const wasReproofing = useRef(false);
  useEffect(() => {
    if (wasReproofing.current && !reproofActive) {
      qc.invalidateQueries({ queryKey: ["result", projectId] });
      qc.invalidateQueries({ queryKey: ["script-check", projectId] });
      // a re-proof re-cuts the chapter's words: the marks meet them afresh
      qc.invalidateQueries({ queryKey: ["script-notes", projectId] });
      qc.invalidateQueries({ queryKey: ["script-snippet", projectId] });
    }
    wasReproofing.current = reproofActive;
  }, [reproofActive, projectId, qc]);

  const fetchResult = (cid: string) =>
    api<ChapterResult>(`/api/modules/proofing/projects/${projectId}/chapters/${cid}/result`);
  const { data: result, isLoading } = useQuery({
    queryKey: ["result", projectId, currentId],
    queryFn: () => fetchResult(currentId!),
    enabled: !!currentId,
  });

  // This file's versions (the same list the project page shows), for the
  // version switcher: found by the chapter on show, which is any version's.
  const { data: audioAssets } = useQuery({
    queryKey: ["audio", projectId],
    queryFn: () =>
      api<{ assets: AudioAssetDto[] }>(`/api/projects/${projectId}/audio`).then((r) => r.assets),
    staleTime: 30_000,
  });
  const thisAsset = useMemo(() => {
    const fn = project?.chapters.find((c) => c.id === currentId)?.audio_filename;
    return audioAssets?.find((a) => a.versions?.some((v) => v.chapter_id === currentId))
      ?? audioAssets?.find((a) => a.filename === fn);
  }, [audioAssets, project, currentId]);
  // Use another version of this file, and open its own report.
  const switchVersion = useMutation({
    mutationFn: (v: AudioVersionDto) =>
      api(`/api/projects/${projectId}/audio/promote`, { method: "POST", body: JSON.stringify({ filename: v.filename }) }),
    onSuccess: (_r, v) => {
      for (const key of ["audio", "project", "spec", "find", "flags", "pickups"]) qc.invalidateQueries({ queryKey: [key, projectId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      if (v.chapter_id && v.chapter_id !== currentId) navigate(`/projects/${projectId}/report/${v.chapter_id}`);
      toast(`Now using version ${v.version} of this file`, "success");
    },
    onError: (e: any) => toast(e.message || "Couldn't switch version.", "error"),
  });

  // Review state: "picked" is server-side (Pickups); dismiss/comment/meaning/
  // category/manual sync server-side too (see lib/reviewState) so review work
  // follows the project across devices. localStorage is the instant-paint cache.
  //
  // Other people (or this person in another tab) may be working on the same
  // book, so each save sends only what changed since the report last heard from
  // the server, and takes back the merged state: everyone's work, with any edit
  // made here meanwhile kept on top. Every so often (and on coming back to the
  // tab) it checks for others' saves.
  const [state, setState] = useState<ReportState>(() => loadState(projectId!));
  const base = useRef<ReportState | null>(null);      // the server's state as last heard; null until then
  const baseAt = useRef<string | null>(null);         // ...and when it was saved
  const saving = useRef(false);                       // one save at a time
  const pushTimer = useRef<number | null>(null);
  const latestState = useRef(state);                  // newest state, for saves and an unmount flush
  latestState.current = state;

  // Take on the server's state, keeping the edits made here that it doesn't
  // have yet - what this copy changed from `ours`, the state it started from.
  function adopt(server: ReportState, at: string | null, ours: ReportState) {
    base.current = server;
    baseAt.current = at;
    saveBase(projectId!, server);
    setState((cur) => {
      const pending = diffReviewState(ours, cur);
      const next = pending ? applyReviewChanges(server, pending) : server;
      return sameReviewState(next, cur) ? cur : next;
    });
  }
  function schedulePush(delay = 800) {
    if (pushTimer.current) window.clearTimeout(pushTimer.current);
    pushTimer.current = window.setTimeout(() => { pushTimer.current = null; void pushNow(); }, delay);
  }
  async function pushNow() {
    const from = base.current;
    if (!from || saving.current) return;
    const sent = latestState.current;
    const changes = diffReviewState(from, sent);
    if (!changes) return;
    saving.current = true;
    try {
      const r = await patchReviewState(projectId!, changes);
      adopt(r.state, r.updatedAt, sent);
      qc.setQueryData(["review", projectId], r.state); // keep the project page's tally fresh
    } catch {
      /* offline - tried again on the next edit or check */
    } finally {
      saving.current = false;
    }
    // Edits made while that save was out go next.
    if (base.current && diffReviewState(base.current, latestState.current)) schedulePush(300);
  }
  async function checkForOthers() {
    if (!base.current || saving.current) return;
    if (diffReviewState(base.current, latestState.current)) { void pushNow(); return; }  // ours first
    try {
      const r = await fetchReviewUpdate(projectId!, baseAt.current);
      if (r.state && base.current && !saving.current) adopt(r.state, r.updatedAt, base.current);
    } catch { /* offline - next time */ }
  }
  const checkRef = useRef(checkForOthers);
  checkRef.current = checkForOthers;
  const pushRef = useRef(pushNow);
  pushRef.current = pushNow;

  // On open: the server's state, with whatever this browser changed but never
  // saved put back on top (told apart by the server state it last heard). A
  // browser that has never heard from the server since this way of saving
  // came in merges as before: the server wins per flag, local-only ones stay.
  useEffect(() => {
    base.current = null;
    let cancelled = false, retry = 0;
    const open = () => fetchReviewUpdate(projectId!)
      .then((r) => {
        if (cancelled || !r.state) return;
        const heard = loadBase(projectId!);
        if (heard) adopt(r.state, r.updatedAt, heard);
        else {
          base.current = r.state;
          baseAt.current = r.updatedAt;
          saveBase(projectId!, r.state);
          setState((local) => mergeReviewState(local, r.state!));
        }
        schedulePush();                   // anything the server doesn't have yet goes up
      })
      .catch(() => { if (!cancelled) retry = window.setTimeout(open, 15000); });  // offline: keep trying
    open();
    return () => { cancelled = true; window.clearTimeout(retry); };
  }, [projectId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Every change goes to the local cache at once, and to the server shortly
  // after (once the report has heard from it, so it knows what changed).
  useEffect(() => {
    saveState(projectId!, state);
    if (base.current) schedulePush();
  }, [projectId, state]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Others' work: checked every half minute while the tab is in front, and on
  // coming back to it.
  useEffect(() => {
    const check = () => { if (document.visibilityState === "visible") void checkRef.current(); };
    const timer = window.setInterval(check, 30000);
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
    };
  }, []);

  // Leaving with a save still pending: send it now so it isn't lost.
  useEffect(() => () => {
    if (pushTimer.current) {
      window.clearTimeout(pushTimer.current);
      pushTimer.current = null;
      void pushRef.current();
    }
  }, [projectId]);

  const pickups = usePickups(projectId!);

  // Sync any local "picked" marks the server doesn't have yet UP to the server
  // (so the Pickups Package / exports see them). The local mark is the durable
  // source for the star, so it is deliberately NOT cleared here — clearing it
  // before the upload was confirmed is exactly what dropped picks on a refresh.
  useEffect(() => {
    if (!result || pickups.isLoading) return;
    const stale = result.discrepancies.filter(
      (d) => state.marks[discrepancyKey(d)]?.status === "picked" && !pickups.keys.has(discrepancyKey(d))
    );
    if (!stale.length) return;
    for (const d of stale) {
      pickups.pick({
        flag_key: discrepancyKey(d),
        chapter_id: currentId ?? null,
        chapter_name: result.chapter.name,
        audio_filename: project?.chapters.find((c) => c.id === currentId)?.audio_filename ?? null,
        flag_type: d.type, page: d.page ?? null,
        category: state.marks[discrepancyKey(d)]?.category ?? categoryOf(d.type),
        script_text: d.script_text ?? "", heard_text: d.heard_text ?? "",
        context_before: d.context_before ?? "", context_after: d.context_after ?? "",
        title: state.marks[discrepancyKey(d)]?.title ?? headlineFor(d),
        comment: state.marks[discrepancyKey(d)]?.comment ?? d.comment ?? "",
        start_time: d.start_time ?? null, end_time: d.end_time ?? null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, pickups.isLoading]);

  // Premium AI analysis job (classifies meaning impact for the whole title).
  const [analyzeJobId, setAnalyzeJobId] = useState<string | null>(null);
  const startAnalyze = useMutation({
    mutationFn: (force: boolean) =>
      api<{ job_id: string }>(`/api/projects/${projectId}/jobs`, {
        method: "POST",
        body: JSON.stringify({ module_id: "proofing", job_type: "analyze", params: { force } }),
      }),
    onSuccess: (r) => setAnalyzeJobId(r.job_id),
    onError: (e: any) => toast(e.message || "Could not start analysis", "error"),
  });
  function onAnalyzeDone(status: string) {
    setAnalyzeJobId(null);
    qc.invalidateQueries({ queryKey: ["result", projectId] });
    toast(status === "completed" ? "Meaning analysis is in." : "Analysis stopped — check the log.",
      status === "completed" ? "success" : "error");
  }

  // Correcting a boundary saves the page instantly; a chapter's start+end edits
  // fold into ONE background re-proof (debounced), so the reviewer never waits.
  const { reproofState, scheduleReproof, flushReproof } = useBoundaryReproof(projectId);
  // Leaving the chapter flushes any pending re-proof so the reviewer doesn't sit
  // through the debounce just to move on.
  useEffect(() => () => { flushReproof(); }, [currentId]);

  // Which Audio start/end review drawer is open (toggled from the strip).
  const [boundaryDrawer, setBoundaryDrawer] = useState<"start" | "end" | null>(null);
  // General notes drawer — shares the right-hand slide-in slot with the
  // boundary drawer, so opening one closes the other.
  const [notesOpen, setNotesOpen] = useState(false);
  // Importing Audition markers — a dialog over the report.
  const [importingMarkers, setImportingMarkers] = useState(false);
  // "No issues" on every clean file — a confirmation dialog over the report.
  const [noIssuesOpen, setNoIssuesOpen] = useState(false);

  const [activeTypes, setActiveTypes] = useState<Set<string>>(new Set(ALL_TYPES));
  const [search, setSearch] = useState("");
  const [showDismissed, setShowDismissed] = useState(false);
  // Auto-filtered false-positives (punctuation/numeric/homophone equivalents)
  // are hidden by default, parallel to the desktop "Filtered (N)" toggle.
  const [showFiltered, setShowFiltered] = useState(false);
  // Only the flags where the script has a note, comment or mark beside the words.
  const [onlyMarked, setOnlyMarked] = useState(false);
  // Flags whose script note was put away ("Keep the flag") - for this visit only.
  const [scriptHidden, setScriptHidden] = useState<Set<string>>(() => new Set());
  // Meaning-impact triage filter — all categories shown by default (purely
  // additive; nothing is hidden until the user narrows it).
  const [activeMeanings, setActiveMeanings] = useState<Set<string>>(
    new Set(["substantial", "slight", "none", "unset"])
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Bumped on every select (even re-selecting the same flag) so the manuscript
  // re-scrolls and audio re-seeks reliably — no need to "click away and back".
  const [selectTick, setSelectTick] = useState(0);
  const [activeId, setActiveId] = useState<string | null>(null);
  // A just-created manual flag whose title editor should open automatically.
  const [autoEditId, setAutoEditId] = useState<string | null>(null);
  // Keyboard-shortcuts cheat-sheet popover (toggled by the Shortcuts button or "?").
  const [showKbdHelp, setShowKbdHelp] = useState(false);
  const player = useRef<AudioHandle>(null);
  const currentTime = useRef(0);

  // Undo stack — each entry knows how to reverse exactly one review action
  // (local state snapshot + any server-side pickup change). undoDepth mirrors
  // its length so the toolbar button can disable when empty. Reset per chapter.
  const undoRef = useRef<Array<{ label: string; run: () => void }>>([]);
  const [undoDepth, setUndoDepth] = useState(0);
  // ...except in Find mode, which moves between files under you: a flag added
  // in the last file can still be undone in this one.
  const finding = useRef(false);
  useEffect(() => { if (!finding.current) { undoRef.current = []; setUndoDepth(0); } }, [currentId]);

  // The same (empty) list on every render when a chapter has no manual flags: a
  // fresh [] each time rebuilt the whole flag list whenever the report redrew.
  const manualFlags = (currentId && state.manual[currentId]) || NO_MANUAL_FLAGS;

  // Build the reviewed-finding list (discrepancies + manual flags), resolving
  // effective status from per-instance marks plus title-wide dismiss rules.
  const reviewed: ReviewedFinding[] = useMemo(() => {
    if (!result) return [];
    const dismissedSigs = new Set(state.dismissed_signatures || []);
    const resolve = (d: Discrepancy, key: string) => {
      const entry = state.marks[key];
      const signature = signatureOf(d);
      // "picked" lives on the server (pickups) AND is mirrored to a durable
      // local mark, so a hard refresh keeps the star even before the server
      // round-trip returns (or if it never did). Picked wins over a dismiss.
      let status: "none" | "dismissed" | "picked" = "none";
      let dismissedByRule = false;
      if (pickups.keys.has(key) || entry?.status === "picked") {
        status = "picked";
      } else if (entry?.status === "dismissed") {
        status = "dismissed";
      } else if (dismissedSigs.has(signature)) {
        status = "dismissed";
        dismissedByRule = true;
      }
      return { entry, signature, status, dismissedByRule };
    };

    // Effective meaning = manual override if set, else the AI's auto value.
    const effMeaning = (entry: any, d: Discrepancy) => {
      const manual = (entry?.meaning as MeaningClass) ?? null;
      const auto = (d.auto_meaning as MeaningClass) ?? null;
      const meaning = manual ?? auto;
      return { meaning, meaningIsAuto: manual == null && auto != null, autoRationale: d.auto_rationale ?? null };
    };

    const out: ReviewedFinding[] = [];
    // Real discrepancies: auto-generated headline; the editor edits the comment.
    for (const d of result.discrepancies) {
      const key = discrepancyKey(d);
      const { entry, signature, status, dismissedByRule } = resolve(d, key);
      const comment = entry?.comment ?? d.comment ?? "";
      out.push({
        d, key, signature, status, dismissedByRule, ...effMeaning(entry, d),
        reason: status === "dismissed" && !dismissedByRule ? entry?.reason : undefined,
        category: entry?.category ?? categoryOf(d.type),
        // Effective title = user override (if any) else the generated headline.
        headline: entry?.title ?? headlineFor(d), comment, editText: comment,
      });
    }
    // Manual flags: stored text is "title\nbody" — first line is the title
    // (headline), the rest is the body. The editor shows the whole thing.
    for (const mf of manualFlags) {
      const text = mf.comment || "";
      const nl = text.indexOf("\n");
      const title = (nl === -1 ? text : text.slice(0, nl)).trim() || "Manual flag";
      const body = (nl === -1 ? "" : text.slice(nl + 1)).trim();
      const d: Discrepancy = {
        id: mf.id, type: "manual" as any, page: mf.page, start_time: mf.start_time,
        end_time: mf.end_time, script_text: mf.script_text ?? "", heard_text: title,
        // A selection-anchored flag carries its token span → highlights in the
        // manuscript like an engine flag (via ManuscriptPane's mark logic).
        manuscript_token_indices: mf.manuscript_token_indices,
      };
      const key = `manual:${mf.id}`;
      const { entry, signature, status, dismissedByRule } = resolve(d, key);
      out.push({
        d, key, signature, status, dismissedByRule, ...effMeaning(entry, d),
        reason: status === "dismissed" && !dismissedByRule ? entry?.reason : undefined,
        category: entry?.category ?? categoryOf(d.type),
        headline: title, comment: body, editText: text,
      });
    }
    // Where in the manuscript a timestamp falls. A by-ear flag has no selected
    // span — it is a moment, not a phrase — so the anchor is derived from the
    // per-token audio times the payload already carries. Without it the flag has
    // nothing to highlight and is invisible on the manuscript side, which is the
    // one place a reviewer looks to judge it.
    const tokens = result.manuscript?.tokens || [];
    const timedIdx: number[] = [];
    const timedAt: number[] = [];
    tokens.forEach((tk: { audio_time?: number | null }, i: number) => {
      if (tk.audio_time != null) { timedIdx.push(i); timedAt.push(tk.audio_time); }
    });
    const spanAt = (at: number | null): number[] | undefined => {
      if (at == null || !timedAt.length) return undefined;
      let lo = 0, hi = timedAt.length - 1, ans = -1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (timedAt[mid] <= at) { ans = mid; lo = mid + 1; } else hi = mid - 1;
      }
      const first = timedIdx[Math.max(0, ans)];
      // Six words — the same span the server quotes into `script_text`, so the
      // highlight and the excerpt describe the same place.
      //
      // Emitting `global_index`, NOT the array position: the pane resolves these
      // through a map keyed by global_index. The two usually coincide, which is
      // precisely why assuming it would fail quietly on the manuscript where it
      // doesn't.
      const out: number[] = [];
      for (let i = first; i < Math.min(tokens.length, first + 6); i++) {
        const gi = (tokens[i] as { global_index?: number }).global_index;
        out.push(typeof gi === "number" ? gi : i);
      }
      return out.length ? out : undefined;
    };
    // An imported marker's stored words, where they are NOW: its indices point
    // at the chapter's words as they were when it came in, and a re-proof since
    // can re-cut them. Null when its words can't be found (the moment then does).
    const storedSpan = (pk: Pickup): number[] | undefined => {
      const sp = settleSpan(tokens, pk.manuscript_token_indices, {
        script: pk.script_text, before: pk.context_before, after: pk.context_after,
      });
      if (!sp) return undefined;
      const out: number[] = [];
      for (let i = sp.first; i <= sp.last; i++) {
        const gi = (tokens[i] as { global_index?: number }).global_index;
        out.push(typeof gi === "number" ? gi : i);
      }
      return out;
    };

    // Flags raised by ear on the PHONE. They are pickups the moment they are
    // tapped — there is no report-review step on a walk — so they exist only
    // server-side and would otherwise reach the Pickups Package without ever
    // appearing here, which is the one place you can judge them against the
    // manuscript. Deduped by key, so a starred desktop manual flag (already
    // added above under the same flag_key) is not listed twice.
    const already = new Set(out.map((o) => o.key));
    for (const pk of pickups.list) {
      if (pk.flag_type !== "manual" || already.has(pk.flag_key)) continue;
      if (!currentId || pk.chapter_id !== currentId) continue;
      // Two writers, two shapes. The phone puts its note in `comment` and leaves
      // `title` empty; the report's editor puts the headline in `title` and the
      // rest in `comment`. Reading only `comment` meant that editing a phone
      // flag here made its text vanish behind the placeholder — the note was
      // safely stored, just never read back.
      const stored = pk.title?.trim() || "";
      const rest = pk.comment || "";
      const nl = rest.indexOf("\n");
      const title = stored || (nl === -1 ? rest : rest.slice(0, nl)).trim() || "Flagged while listening";
      const body = stored ? rest.trim() : (nl === -1 ? "" : rest.slice(nl + 1)).trim();
      const text = body ? `${title}\n${body}` : title;
      const d: Discrepancy = {
        id: pk.flag_key, type: "manual" as any, page: pk.page,
        start_time: pk.start_time, end_time: pk.end_time,
        // The excerpt the server filled in from the manuscript at that second,
        // so the row reads as a place in the book rather than a bare timestamp.
        script_text: pk.script_text || "", heard_text: title,
        context_before: pk.context_before, context_after: pk.context_after,
        // A marker imported from Audition knows its words (the ones it quoted);
        // a flag raised by ear only knows its moment.
        manuscript_token_indices: storedSpan(pk) ?? spanAt(pk.start_time),
      };
      const { entry, signature, status, dismissedByRule } = resolve(d, pk.flag_key);
      out.push({
        d, key: pk.flag_key, signature, status, dismissedByRule, ...effMeaning(entry, d),
        // An imported marker arrives already typed by its hashtag (Noise, …).
        category: entry?.category ?? storedCategory(pk.category) ?? categoryOf(d.type),
        headline: title, comment: body, editText: text,
      });
    }
    // Pickups from an earlier proof of this file that this proof didn't find -
    // the file was re-proofed (its audio replaced, a boundary moved) and the
    // finding wasn't there to be picked again. They are still pickups, in the
    // Package and the exports; here they get a row of their own, placed where
    // their words are now (the server re-timed them, pickup_settle), so they
    // can be played in this audio and kept or let go. They used to vanish.
    const live = new Set(result.discrepancies.map((x) => discrepancyKey(x)));
    for (const pk of pickups.list) {
      if (pk.flag_type === "manual" || !currentId || pk.chapter_id !== currentId) continue;
      if (live.has(pk.flag_key) || already.has(pk.flag_key)) continue;
      const d: Discrepancy = {
        id: pk.flag_key, type: (pk.flag_type || "substitution") as any, page: pk.page,
        start_time: pk.start_time, end_time: pk.end_time,
        script_text: pk.script_text || "", heard_text: pk.heard_text || "",
        context_before: pk.context_before, context_after: pk.context_after,
        manuscript_token_indices: storedSpan(pk) ?? spanAt(pk.start_time),
      };
      const { entry, signature, status, dismissedByRule } = resolve(d, pk.flag_key);
      const comment = entry?.comment ?? pk.comment ?? "";
      out.push({
        d, key: pk.flag_key, signature, status, dismissedByRule, ...effMeaning(entry, d),
        category: entry?.category ?? storedCategory(pk.category) ?? categoryOf(d.type),
        headline: entry?.title ?? (pk.title?.trim() || headlineFor(d)), comment, editText: comment,
        kept: true,
      });
    }
    return out;
  }, [result, state, manualFlags, pickups.keys, pickups.list, currentId]);

  // Per-category counts reflect REAL issues (auto-filtered items are excluded so
  // the numbers match what you act on); the separate Filtered chip counts them.
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const f of reviewed) if (!f.d.filtered) {
      c[f.category] = (c[f.category] || 0) + 1;
    }
    return c;
  }, [reviewed]);

  const filteredCount = useMemo(
    () => reviewed.filter((f) => f.d.filtered && f.status !== "dismissed").length,
    [reviewed]
  );

  // Counts per meaning-impact bucket (+ "unset") for the triage filter chips.
  const meaningCounts = useMemo(() => {
    const c: Record<string, number> = { substantial: 0, slight: 0, none: 0, unset: 0 };
    for (const f of reviewed) c[f.meaning ?? "unset"]++;
    return c;
  }, [reviewed]);

  // The script's own marks beside this chapter's flags (notes, comments, struck
  // words, highlights, marks drawn on the page) - see ScriptNoteCard.
  const { data: scriptNotes } = useScriptNotes(projectId, currentId);
  const scriptMarks = useMemo(() => marksByFlag(reviewed, scriptNotes?.marks), [reviewed, scriptNotes]);
  const markedCount = useMemo(
    () => reviewed.filter((f) => scriptMarks.has(f.d.id) && f.status !== "dismissed" && !f.d.filtered).length,
    [reviewed, scriptMarks]
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return reviewed
      .filter((f) => !onlyMarked || scriptMarks.has(f.d.id))
      .filter((f) => activeTypes.has(f.category))
      .filter((f) => activeMeanings.has(f.meaning ?? "unset"))
      .filter((f) => showFiltered || !f.d.filtered)
      .filter((f) => showDismissed || f.status !== "dismissed")
      .filter((f) =>
        !q ||
        (f.comment + f.d.script_text + f.d.heard_text).toLowerCase().includes(q)
      )
      .sort((a, b) => (a.d.start_time ?? 1e9) - (b.d.start_time ?? 1e9));
  }, [reviewed, activeTypes, activeMeanings, showFiltered, showDismissed, search, onlyMarked, scriptMarks]);

  // Discrepancies that should highlight in the manuscript (visible, not dismissed).
  const highlightDiscs = useMemo(
    () => visible.filter((f) => f.status !== "dismissed").map((f) => f.d),
    [visible]
  );
  // Each highlighted flag's current category, so its words match its card's
  // colour after a re-label (a manual flag set to Misread, a mis-detected one
  // corrected) instead of keeping the colour of the type it was created as.
  const highlightCats = useMemo(
    () => new Map(visible.map((f) => [f.d.id, f.category as string])),
    [visible]
  );

  const timeIndex = useMemo(
    () =>
      (result?.discrepancies || [])
        .filter((d) => d.start_time != null)
        .map((d) => ({ id: d.id, s: d.start_time as number, e: (d.end_time ?? d.start_time) as number })),
    [result]
  );

  // ---- actions ----
  function setMark(key: string, patch: { status?: any; title?: string; comment?: string; meaning?: MeaningClass; category?: FlagCategory; reason?: "script" }) {
    setState((s) => ({ ...s, marks: { ...s.marks, [key]: { ...s.marks[key], ...patch } } }));
  }

  // ---- undo ----
  function pushUndo(label: string, run: () => void) {
    undoRef.current.push({ label, run });
    if (undoRef.current.length > 50) undoRef.current.shift();
    setUndoDepth(undoRef.current.length);
  }
  function undoLast() {
    const cmd = undoRef.current.pop();
    setUndoDepth(undoRef.current.length);
    if (!cmd) { toast("Nothing to undo", "info"); return; }
    cmd.run();
    toast(`Undid: ${cmd.label}`, "info");
  }
  // Run a review action and record its inverse. `mutate` performs the change and
  // may return a function that reverses any *server-side* pickup change it made;
  // the report state is reversed by undoing exactly what the action changed.
  function act(label: string, mutate: () => void | (() => void)) {
    const before = state;
    let after: ReportState | null = null;
    const reverse = mutate();
    setState((s) => { after = s; return s; });   // the state once this action has applied
    pushUndo(label, () => {
      // Take back just this action's changes. Putting back the whole earlier
      // copy would also take back other people's work that arrived since.
      setState((cur) => {
        const back = after ? diffReviewState(after, before) : null;
        return back ? applyReviewChanges(cur, back) : cur;
      });
      reverse?.();
    });
  }

  function dismiss(key: string) {
    act("dismiss", () => {
      let reverse: (() => void) | undefined;
      if (pickups.keys.has(key)) {
        const f = reviewed.find((x) => x.key === key);
        const snap = f ? snapshotFrom(key, f.d, f.comment, f.headline) : null;
        pickups.unpick(key); // dismissing un-picks it
        if (snap) reverse = () => pickups.pick(snap);
      }
      const cur = state.marks[key]?.status;
      setMark(key, { status: cur === "dismissed" ? "none" : "dismissed", reason: undefined });
      return reverse;
    });
  }
  // "Intended, per the script": the narrator read what the script's own note
  // asked for. Clears the flag - dismissed, with that reason - and un-picks it;
  // pressed again (Undo on a cleared card) it restores the flag.
  function intended(key: string) {
    const was = state.marks[key];
    const undoing = was?.status === "dismissed" && was?.reason === "script";
    act(undoing ? "restore flag" : "intended, per the script", () => {
      let reverse: (() => void) | undefined;
      if (!undoing && pickups.keys.has(key)) {
        const f = reviewed.find((x) => x.key === key);
        const snap = f ? snapshotFrom(key, f.d, f.comment, f.headline) : null;
        pickups.unpick(key);
        if (snap) reverse = () => pickups.pick(snap);
      }
      setMark(key, undoing ? { status: "none", reason: undefined } : { status: "dismissed", reason: "script" });
      return reverse;
    });
    toast(undoing ? "Flag restored." : "Cleared: intended, per the script — not a pickup.", undoing ? "info" : "success");
  }
  function toggleScriptHidden(id: string) {
    setScriptHidden((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  // Dismiss (or restore) every identical flag across the whole title via a
  // content-signature rule — applies to other chapters as you open them.
  function dismissAllIdentical(d: Discrepancy) {
    const sig = signatureOf(d);
    const has = (state.dismissed_signatures || []).includes(sig);
    const nHere = reviewed.filter((f) => f.signature === sig).length;
    act(has ? "restore identical" : "dismiss identical", () => {
      setState((s) => {
        const set = new Set(s.dismissed_signatures || []);
        if (has) set.delete(sig);
        else set.add(sig);
        return { ...s, dismissed_signatures: Array.from(set) };
      });
    });
    toast(
      has
        ? "Restored all identical flags across the title"
        : `Dismissed all identical flags (${nHere} here + any in other chapters)`,
      has ? "info" : "success"
    );
  }
  function pickup(key: string) {
    // Picked if the server has it OR the durable local mark says so (the local
    // mark is what survives a hard refresh before/without the server round-trip).
    const isPicked = pickups.keys.has(key) || state.marks[key]?.status === "picked";
    act(isPicked ? "un-pick" : "pick up", () => {
      const f = reviewed.find((x) => x.key === key);
      if (isPicked) {
        const snap = f ? snapshotFrom(key, f.d, f.comment, f.headline) : null;
        pickups.unpick(key);
        setMark(key, { status: undefined }); // clear the durable local pick
        return snap ? () => pickups.pick(snap) : undefined; // local restored by undo's setState
      }
      if (f) pickups.pick(snapshotFrom(key, f.d, f.comment, f.headline));
      setMark(key, { status: "picked" }); // durable local pick — survives refresh; also un-dismisses
      return () => pickups.unpick(key);
    });
  }
  // A pickup's stored category, when it's one the report knows. Flags created
  // server-side (imported markers) carry their type only there, so every
  // re-send of their snapshot must fall back to it rather than to "manual".
  function storedCategoryOf(key: string): FlagCategory | undefined {
    return storedCategory(pickups.list.find((p) => p.flag_key === key)?.category);
  }
  function snapshotFrom(key: string, d: Discrepancy, comment = "", title = "", category?: FlagCategory): PickupSnapshot {
    return {
      flag_key: key,
      chapter_id: currentId ?? null,
      chapter_name: result?.chapter.name ?? null,
      audio_filename: project?.chapters.find((c) => c.id === currentId)?.audio_filename ?? null,
      flag_type: d.type,
      // Effective display category: the explicit override (from setCategory), else
      // the current per-flag mark, else derived from the raw type. So every pickup
      // write carries the right category for the Package + exports.
      category: category ?? state.marks[key]?.category ?? storedCategoryOf(key) ?? categoryOf(d.type),
      page: d.page ?? null,
      script_text: d.script_text ?? "",
      heard_text: d.heard_text ?? "",
      context_before: d.context_before ?? "",
      context_after: d.context_after ?? "",
      title, // one-line headline → exports' Title column
      comment, // narrative explanation → exports' Comment column + marker notes
      start_time: d.start_time ?? null,
      end_time: d.end_time ?? null,
    };
  }
  function editComment(key: string, text: string) {
    const f = reviewed.find((x) => x.key === key);
    if (f && f.editText === text) return; // unchanged on blur → nothing to record
    // If a real flag is already picked, its server snapshot's comment must track
    // the edit so exports stay current; the undo re-upserts the previous comment.
    const syncKey = f && !key.startsWith("manual:") && pickups.keys.has(key) ? key : null;
    const syncD = f?.d;
    const oldComment = f?.comment ?? "";
    const title = f?.headline ?? "";
    act("edit comment", () => {
      // A manual flag's editable text IS its title/headline — update the flag
      // itself so the one-liner reflects it (rather than a separate comment).
      if (key.startsWith("manual:") && currentId) {
        const id = key.slice("manual:".length);
        // Store the raw "title\nbody" text; display splits it (an empty title
        // falls back to "Manual flag").
        setState((s) => ({
          ...s,
          manual: {
            ...s.manual,
            [currentId]: (s.manual[currentId] || []).map((m) =>
              m.id === id ? { ...m, comment: text } : m
            ),
          },
        }));
        // If this manual flag is picked, keep its server pickup in step so the
        // Package + exports show the edited title (first line) + body.
        if (pickups.keys.has(key)) {
          // A flag raised on the phone has NO local manual entry — it was a
          // pickup from the moment it was tapped. Falling through with an
          // undefined `mf` wrote null/"" over its timestamp, page and excerpt,
          // which is why editing the note dropped it to the end of the list and
          // stopped it playing. The stored pickup is the base in that case.
          const mf = (state.manual[currentId] || []).find((m) => m.id === id)
            ?? pickups.list.find((pk) => pk.flag_key === key);
          const mkSnap = (raw: string) => {
            const nl = raw.indexOf("\n");
            const t = (nl === -1 ? raw : raw.slice(0, nl)).trim() || "Manual flag";
            const b = (nl === -1 ? "" : raw.slice(nl + 1)).trim();
            const d = {
              id, type: "manual", page: mf?.page ?? null,
              start_time: mf?.start_time ?? null, end_time: mf?.end_time ?? null,
              // Preserve the selection's manuscript text + context so the Package
              // shows the highlighted fragment, not the title. (Was hardcoded ""
              // here, which wiped the selection whenever the note was edited.)
              script_text: mf?.script_text ?? "", heard_text: t,
              context_before: mf?.context_before ?? "", context_after: mf?.context_after ?? "",
            } as unknown as Discrepancy;
            return snapshotFrom(key, d, b, t);
          };
          pickups.pick(mkSnap(text));
          const oldText = f?.editText ?? "";
          return () => pickups.pick(mkSnap(oldText));
        }
        return;
      }
      setMark(key, { comment: text });
      if (syncKey && syncD) {
        pickups.pick(snapshotFrom(syncKey, syncD, text, title));
        return () => pickups.pick(snapshotFrom(syncKey, syncD, oldComment, title));
      }
    });
  }
  // Edit a real flag's one-line title (headline). Stored as a per-flag override
  // in localStorage; manual flags keep their title in their own text instead.
  // An empty title clears the override (falls back to the generated headline).
  function setTitle(key: string, value: string) {
    const f = reviewed.find((x) => x.key === key);
    if (!f) return;
    const next = value.trim();
    if (next === f.headline) return; // unchanged → nothing to record
    const syncKey = !key.startsWith("manual:") && pickups.keys.has(key) ? key : null;
    const syncD = f.d;
    const oldTitle = f.headline;
    const comment = f.comment;
    act("edit title", () => {
      setMark(key, { title: next || undefined });
      if (syncKey) {
        pickups.pick(snapshotFrom(syncKey, syncD, comment, next || headlineFor(syncD)));
        return () => pickups.pick(snapshotFrom(syncKey, syncD, comment, oldTitle));
      }
    });
  }
  // Re-assign a mis-detected flag to a different category (overrides the
  // engine's type for display, filtering, counts and the picked-CSV export).
  function setCategory(key: string, value: FlagCategory) {
    const f = reviewed.find((x) => x.key === key);
    if (!f || f.category === value) return;
    // Keep a picked flag's server pickup in step so the Package + exports show
    // the new category (esp. a manual flag re-labelled Misread/etc.).
    const syncKey = pickups.keys.has(key) ? key : null;
    const oldCat = f.category;
    act("change category", () => {
      setMark(key, { category: value });
      if (syncKey) {
        pickups.pick(snapshotFrom(key, f.d, f.comment, f.headline, value));
        return () => pickups.pick(snapshotFrom(key, f.d, f.comment, f.headline, oldCat));
      }
    });
  }
  function toggleType(t: string) {
    setActiveTypes((cur) => {
      const next = new Set(cur);
      next.has(t) ? next.delete(t) : next.add(t);
      return next;
    });
  }
  function toggleMeaning(m: string) {
    setActiveMeanings((cur) => {
      const next = new Set(cur);
      next.has(m) ? next.delete(m) : next.add(m);
      return next;
    });
  }
  // Classify a flag's meaning impact (or clear it with "").
  function setMeaning(key: string, value: MeaningClass | "") {
    act("rate impact", () => { setMark(key, { meaning: value || undefined }); });
  }
  function selectFinding(id: string) {
    setSelectedId(id);
    setSelectTick((n) => n + 1); // force scroll/seek even if id is unchanged
    // `reviewed` is the fallback because a flag raised on the phone is in
    // NEITHER of the other two: it is not an engine discrepancy and it never
    // had a local manual entry. Without this it silently refused to play in the
    // report while playing perfectly well from the Pickups Package.
    const d = (result?.discrepancies || []).find((x) => x.id === id) ||
      manualFlags.find((m) => m.id === id) ||
      reviewed.find((x) => x.key === id || x.d.id === id)?.d;
    const t = (d as any)?.start_time;
    if (t != null) player.current?.playFrom(t);
  }
  function addManualFlag(atTime: number) {
    if (!currentId) return;
    const id = `mf_${Date.now()}`;
    const flag: ManualFlag = {
      id, type: "manual", page: null, start_time: atTime, end_time: atTime,
      comment: "", // empty until named; display falls back to "Manual flag"
    };
    act("add flag", () => {
      setState((s) => ({
        ...s,
        manual: { ...s.manual, [currentId]: [...(s.manual[currentId] || []), flag] },
      }));
    });
    // Select it and open its title editor immediately so you can name it.
    setSelectedId(id);
    setAutoEditId(id);
    toast(`Flag added at ${formatTime(atTime)} — name it`, "success");
  }
  // "Flag here" from a manuscript text selection — a manual flag anchored to the
  // selected tokens, so it highlights in the manuscript and shows its fragment
  // highlighted in the Pickups Package, just like an engine-detected flag.
  function flagFromSelection(sel: SelectionFlagInput) {
    if (!currentId) return;
    const id = `mf_${Date.now()}`;
    const flag: ManualFlag = {
      id, type: "manual", page: sel.page,
      start_time: sel.startTime, end_time: sel.endTime, comment: "",
      script_text: sel.text,
      context_before: sel.contextBefore,
      context_after: sel.contextAfter,
      manuscript_token_indices: sel.tokenIndices,
    };
    act("add flag", () => {
      setState((s) => ({
        ...s,
        manual: { ...s.manual, [currentId]: [...(s.manual[currentId] || []), flag] },
      }));
    });
    setSelectedId(id);
    setAutoEditId(id);
    const preview = sel.text.length > 32 ? sel.text.slice(0, 32) + "…" : sel.text;
    toast(`Flagged “${preview}” — add a note`, "success");
  }
  // Copy a finding's text (its comment, else the headline) to the clipboard —
  // the keyboard equivalent of the row's ⧉ button.
  function copyFinding(f: ReviewedFinding) {
    const text = f.editText || f.headline;
    navigator.clipboard?.writeText(text).then(
      () => toast("Comment copied", "success"),
      () => toast("Couldn't copy", "error")
    );
  }
  // The most-recently-actioned flag + its neighbours' ids, captured at action
  // time. When an action drops that flag out of the visible list (dismiss /
  // dismiss-all), this lets ↓/↑ resume from where it WAS instead of snapping to
  // the top.
  const anchorRef = useRef<{ id: string; prevId: string | null; nextId: string | null } | null>(null);
  function recordAnchor(id: string) {
    const i = visible.findIndex((f) => f.d.id === id);
    anchorRef.current = {
      id,
      prevId: i > 0 ? visible[i - 1].d.id : null,
      nextId: i >= 0 && i < visible.length - 1 ? visible[i + 1].d.id : null,
    };
  }
  // Move the selection through the currently-visible flags (and play + scroll to
  // it). If the selected flag is still visible, move relative to it. If it just
  // left the list (you dismissed it), resume from its remembered neighbour rather
  // than jumping back to the first flag. From no selection, ↓ = first, ↑ = last.
  function moveSelection(delta: number) {
    if (!visible.length) return;
    const i = visible.findIndex((f) => f.d.id === selectedId);
    if (i !== -1) {
      const n = Math.min(visible.length - 1, Math.max(0, i + delta));
      selectFinding(visible[n].d.id);
      return;
    }
    const a = anchorRef.current;
    if (a && a.id === selectedId) {
      const wantId = delta > 0 ? a.nextId : a.prevId;
      const altId = delta > 0 ? a.prevId : a.nextId; // at an edge: fall back the other way
      const target =
        (wantId ? visible.find((f) => f.d.id === wantId) : undefined) ||
        (altId ? visible.find((f) => f.d.id === altId) : undefined);
      if (target) { selectFinding(target.d.id); return; }
    }
    selectFinding(visible[delta > 0 ? 0 : visible.length - 1].d.id);
  }

  // ---- Find mode ----------------------------------------------------------
  // "Find all of these in the manuscript": every place the selected words are
  // in the book, one after another, across files. The report follows the hit
  // you're on - moving to one in another file opens that file - so the flag
  // actions, the manuscript and the audio are always that file's own.
  const [find, setFind] = useState<FindState | null>(null);
  finding.current = !!find;
  const [findAutoplay, setFindAutoplay] = useState(readFindAutoplay);
  const [findNote, setFindNote] = useState("");
  const findRes = useFind(projectId, find?.query ?? "", !!find);
  const hits = findRes.data?.hits ?? NO_HITS;
  const hit = find && find.index >= 0 ? hits[find.index] : undefined;
  // The report is showing the hit's own file (its flags and audio are here).
  const hitHere = !!hit?.chapter_id && result?.chapter.id === hit.chapter_id;
  const { data: pdfPages } = useManuscriptPages(find ? projectId : undefined);

  function startFind(sel: SelectionFlagInput) {
    setFind({
      query: sel.query.trim(), index: -1, tick: 0, play: false,
      from: currentId && sel.tokenIndices.length ? { chapterId: currentId, token: sel.tokenIndices[0] } : undefined,
    });
    setSelectedId(null);
  }
  function searchAgain(q: string) {
    setFind({
      query: q, index: -1, tick: 0, play: false,
      from: hit?.chapter_id ? { chapterId: hit.chapter_id, token: hit.tokens[0] } : undefined,
    });
  }
  function openFileOf(h: FindHit) {
    if (h.chapter_id && h.chapter_id !== currentId) navigate(`/projects/${projectId}/report/${h.chapter_id}`, { replace: true });
  }
  // The search answered: start on the words it began from (else this file's first).
  useEffect(() => {
    if (!find || find.index !== -1 || !findRes.data || !hits.length) return;
    const f = find.from;
    let i = f ? hits.findIndex((h) => h.chapter_id === f.chapterId && h.tokens.includes(f.token)) : -1;
    if (i < 0) i = hits.findIndex((h) => h.chapter_id === currentId);
    if (i < 0) i = 0;
    setFind({ ...find, index: i, tick: find.tick + 1, play: false });
    openFileOf(hits[i]);
  }, [find, findRes.data, hits, currentId]);  // eslint-disable-line react-hooks/exhaustive-deps

  function goHit(i: number, play = findAutoplay) {
    if (!find || !hits.length) return;
    const n = Math.max(0, Math.min(hits.length - 1, i));
    if (n === find.index) return;
    setFind((f) => f && { ...f, index: n, tick: f.tick + 1, play });
    setSelectedId(null);
    openFileOf(hits[n]);
  }
  const goHitRef = useRef(goHit);
  goHitRef.current = goHit;

  // Heard: a hit's clip played to its end. Kept with the review state, so a
  // long sweep can be finished another day (or on another machine).
  function markHeard(h: FindHit) {
    const cid = h.chapter_id;
    if (!cid || !h.tokens.length) return;
    setState((s) => {
      const had = new Set(s.heard?.[cid] || []);
      if (h.tokens.every((t) => had.has(t))) return s;
      h.tokens.forEach((t) => had.add(t));
      return { ...s, heard: { ...(s.heard || {}), [cid]: Array.from(had).sort((a, b) => a - b) } };
    });
  }
  function playHit(h: FindHit) {
    const clip = clipOf(h);
    if (!clip || !h.chapter_id || h.chapter_id !== result?.chapter.id) return;
    player.current?.playClip(clip[0], clip[1], () => markHeard(h));
  }
  // Each move acted on once its file is open: played, when you're playing as you go.
  const actedOn = useRef("");
  useEffect(() => {
    if (!find || !hit) return;
    const ask = `${hit.id}#${find.tick}`;
    if (actedOn.current === ask) return;
    if (hit.chapter_id && !hitHere) return;           // its file is still opening
    actedOn.current = ask;
    if (find.play) playHit(hit);
  });  // eslint-disable-line react-hooks/exhaustive-deps
  // The next and previous files, fetched ahead so crossing into them is instant.
  useEffect(() => {
    if (!find || find.index < 0) return;
    for (const j of [find.index + 1, find.index - 1]) {
      const c = hits[j]?.chapter_id;
      if (c && c !== currentId) {
        qc.prefetchQuery({ queryKey: ["result", projectId, c], queryFn: () => fetchResult(c), staleTime: 60_000 });
      }
    }
  }, [find?.index, hits, currentId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // A hit's ticks and flags, for its row and its page's thumbnail.
  const heardSets = useMemo(() => {
    const m = new Map<string, Set<number>>();
    for (const [cid, ts] of Object.entries(state.heard || {})) m.set(cid, new Set(ts));
    return m;
  }, [state.heard]);
  const stateOf = useCallback((h: FindHit): HitState => {
    const cid = h.chapter_id;
    const heardHere = cid ? heardSets.get(cid) : undefined;
    const heard = !!heardHere && h.tokens.length > 0 && h.tokens.every((t) => heardHere.has(t));
    const chips: HitState["chips"] = [];
    let flagged = false;
    const isPicked = (key: string) => pickups.keys.has(key) || state.marks[key]?.status === "picked";
    const mine = cid ? (state.manual[cid] || []).filter((m) => onWords(m.manuscript_token_indices, h.tokens)) : [];
    if (mine.length) {
      flagged = true;
      chips.push({ label: mine.some((m) => isPicked(`manual:${m.id}`)) ? "Flagged · picked up" : "Flagged", cls: "m-manual" });
    }
    const sigs = new Set(state.dismissed_signatures || []);
    for (const f of h.flags) {
      const d = asDiscrepancy(f);
      const key = discrepancyKey(d);
      const cat = state.marks[key]?.category ?? categoryOf(f.type);
      const name = CATEGORY_LABEL[cat] ?? cat;
      if (isPicked(key)) { flagged = true; chips.push({ label: `${name} · picked up`, cls: `m-${cat}` }); }
      else if (state.marks[key]?.status === "dismissed" || sigs.has(signatureOf(d))) chips.push({ label: `${name} · dismissed`, cls: "quiet" });
      else if (f.filtered) chips.push({ label: f.heard_text ? `Filtered · heard “${f.heard_text}”` : "Filtered", cls: "quiet" });
      else chips.push({ label: cat === "misread" && f.heard_text ? `${name} · heard “${f.heard_text}”` : name, cls: `m-${cat}` });
    }
    return { heard, flagged, chips: chips.slice(0, 2) };
  }, [heardSets, state.manual, state.marks, state.dismissed_signatures, pickups.keys]);

  // The flags on the hit you're on - the report's own, to edit right there.
  const hitFlags = useMemo(
    () => (hit && hitHere ? reviewed.filter((f) => onWords(f.d.manuscript_token_indices, hit.tokens)) : []),
    [hit, hitHere, reviewed]
  );
  // Flag the hit you're on: a flag on its words in its own file, with the note,
  // picked up at once - the point of the sweep is the Pickups Package.
  function flagHit(h: FindHit) {
    if (!currentId || !hitHere || h.chapter_id !== currentId) return;
    const id = `mf_${Date.now()}`;
    const key = `manual:${id}`;
    const title = findNote.trim() || `Check “${h.text}”`;
    const before = h.before.trim(), after = h.after.trim();
    const flag: ManualFlag = {
      id, type: "manual", page: h.page, start_time: h.start, end_time: h.end, comment: title,
      script_text: h.text, context_before: before, context_after: after,
      manuscript_token_indices: h.tokens,
    };
    const d = {
      id, type: "manual", page: h.page, start_time: h.start, end_time: h.end,
      script_text: h.text, heard_text: title, context_before: before, context_after: after,
    } as unknown as Discrepancy;
    act("flag", () => {
      setState((s) => ({
        ...s,
        manual: { ...s.manual, [currentId]: [...(s.manual[currentId] || []), flag] },
        marks: { ...s.marks, [key]: { ...s.marks[key], status: "picked" } },
      }));
      pickups.pick(snapshotFrom(key, d, "", title));
      return () => pickups.unpick(key);
    });
    toast(`Flagged and picked up — p. ${h.page ?? "–"}`, "success");
  }
  // Take back the flags you added on this hit (not the proofing's own).
  function unflagHit(h: FindHit) {
    if (!currentId || h.chapter_id !== currentId) return;
    const mine = (state.manual[currentId] || []).filter((m) => onWords(m.manuscript_token_indices, h.tokens));
    if (!mine.length) return;
    const ids = new Set(mine.map((m) => m.id));
    const snaps = mine.map((m) => {
      const key = `manual:${m.id}`;
      const f = reviewed.find((x) => x.key === key);
      return pickups.keys.has(key) && f ? snapshotFrom(key, f.d, f.comment, f.headline) : null;
    });
    act("remove flag", () => {
      setState((s) => {
        const marks = { ...s.marks };
        for (const i of ids) delete marks[`manual:${i}`];
        return { ...s, marks, manual: { ...s.manual, [currentId]: (s.manual[currentId] || []).filter((m) => !ids.has(m.id)) } };
      });
      for (const i of ids) if (pickups.keys.has(`manual:${i}`)) pickups.unpick(`manual:${i}`);
      return () => { for (const sn of snaps) if (sn) pickups.pick(sn); };
    });
    toast("Flag removed", "info");
  }
  // The one button (and F): flag it; or, where the proofing already flagged
  // these words, pick that flag up rather than add a second; or take yours back.
  const findAction = ((): { label: string; icon: "flag" | "check" | "x"; run: () => void } | null => {
    if (!hit || hit.start == null || !hit.chapter_id) return null;
    const open = hitFlags.find((f) => f.status === "none");
    const mine = hitFlags.some((f) => f.key.startsWith("manual:") && f.status === "picked"
      && (state.manual[hit.chapter_id!] || []).some((m) => `manual:${m.id}` === f.key));
    if (hitFlags.every((f) => f.status === "dismissed")) return { label: "Flag", icon: "flag", run: () => flagHit(hit) };
    if (open) return { label: "Pick up this flag", icon: "check", run: () => pickup(open.key) };
    if (mine) return { label: "Remove flag", icon: "x", run: () => unflagHit(hit) };
    return null;
  })();

  // What the manuscript panel and the player are told.
  const paneFind = useMemo<PaneFind | undefined>(
    () => (find && currentId ? {
      hits: hits.filter((h) => h.chapter_id === currentId).map((h) => ({ id: h.id, tokens: h.tokens })),
      current: hit?.id ?? null,
      tick: find.tick,
    } : undefined),
    [find, hits, hit, currentId]
  );
  const playerFind = useMemo(
    () => (find && currentId ? {
      ticks: hits.filter((h) => h.chapter_id === currentId && h.start != null)
        .map((h) => ({ key: h.id, time: h.start as number, now: h.id === hit?.id })),
      clip: hit && hit.chapter_id === currentId ? clipOf(hit) : null,
      onTick: (key: string) => goHitRef.current(hits.findIndex((h) => h.id === key)),
    } : undefined),
    [find, hits, hit, currentId]
  );

  // Keyboard review: drive the whole report from the keys. Chapter/transport
  // keys plus a per-flag review layer — click a flag, then p/d/a/c to act and
  // ↓/↑ (or j/k) to move on. A "latest ref" keeps the listener bound once while
  // always seeing fresh state (visible list, selection, action closures).
  const kbdRef = useRef<(e: KeyboardEvent) => void>(() => {});
  kbdRef.current = (e: KeyboardEvent) => {
    // A dialog over the report owns the keyboard: a stray D or → must not
    // dismiss a flag or change chapter behind it.
    if (importingMarkers || noIssuesOpen) {
      if (e.key === "Escape") { setImportingMarkers(false); setNoIssuesOpen(false); }
      return;
    }
    const tag = (e.target as HTMLElement)?.tagName;
    // In a text field, let the browser handle keys (typing, native ⌘Z, etc.).
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
    const lower = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    if ((e.metaKey || e.ctrlKey) && lower === "z" && !e.shiftKey) {
      e.preventDefault(); undoLast(); return;
    }
    if (e.key === "?") { e.preventDefault(); setShowKbdHelp((v) => !v); return; }
    // Find mode: ↓/↑ through the hits (across files), Enter plays the one
    // you're on, F flags it, Esc leaves. ←/→ would take the file away from
    // under the hit, so they rest while finding.
    if (find) {
      if (e.key === "Escape") {
        e.preventDefault();
        if (showKbdHelp || notesOpen) { setShowKbdHelp(false); setNotesOpen(false); }
        else setFind(null);
        return;
      }
      if (e.key === "Enter") {
        if (tag === "BUTTON" || tag === "A") return;
        e.preventDefault();
        if (hit) playHit(hit);
        return;
      }
      if (!e.metaKey && !e.ctrlKey && !e.altKey) {
        if (e.key === "ArrowDown" || lower === "j") { e.preventDefault(); goHit(find.index + 1); return; }
        if (e.key === "ArrowUp" || lower === "k") { e.preventDefault(); goHit(find.index - 1); return; }
        if (e.key === "ArrowLeft" || e.key === "ArrowRight") return;
        if (lower === "f") { e.preventDefault(); findAction?.run(); return; }
      }
    }
    if (e.key === "Escape") { setShowKbdHelp(false); setNotesOpen(false); return; }
    if (e.code === "Space") { e.preventDefault(); player.current?.toggle(); return; }
    if (e.key === "ArrowRight") { gotoChapter(1); return; }
    if (e.key === "ArrowLeft") { gotoChapter(-1); return; }
    // Enter → replay the selected flag's fragment (same as re-clicking it). Skip
    // when a button/link is focused so Enter still activates it.
    if (e.key === "Enter") {
      if (tag === "BUTTON" || tag === "A") return;
      if (selectedId) { e.preventDefault(); selectFinding(selectedId); }
      return;
    }

    // Per-flag review keys (no command/alt modifier — leave browser shortcuts).
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === "ArrowDown" || lower === "j") { e.preventDefault(); moveSelection(1); return; }
    if (e.key === "ArrowUp" || lower === "k") { e.preventDefault(); moveSelection(-1); return; }
    const f = visible.find((x) => x.d.id === selectedId) || reviewed.find((x) => x.d.id === selectedId);
    if (!f) return;
    // Remember where this flag sits before the action (it may leave the list) so
    // the next ↓/↑ moves on from here, not from the top.
    if (lower === "p") { e.preventDefault(); recordAnchor(f.d.id); pickup(f.key); }
    else if (lower === "d") { e.preventDefault(); recordAnchor(f.d.id); dismiss(f.key); }
    else if (lower === "a") { e.preventDefault(); recordAnchor(f.d.id); dismissAllIdentical(f.d); }
    else if (lower === "c") { e.preventDefault(); recordAnchor(f.d.id); copyFinding(f); }
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => kbdRef.current(e);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function gotoChapter(delta: number) {
    const idx = proofed.findIndex((c) => c.id === currentId);
    const next = proofed[idx + delta];
    if (next) navigate(`/projects/${projectId}/report/${next.id}`);
  }

  function onTime(t: number) {
    const hit = timeIndex.find((x) => t >= x.s - 0.05 && t <= x.e + 0.05);
    setActiveId(hit ? hit.id : null);
  }

  if (!project) return <div className="page muted">Loading…</div>;
  if (proofed.length === 0)
    return (
      <>
        <ProjectHeader projectId={projectId!} name={project.name} active="proofing" />
        <div className="page">
          <NothingProofedYet project={project} projectId={projectId!} />
        </div>
      </>
    );

  const idx = proofed.findIndex((c) => c.id === currentId);

  const credits = result?.chapter.credits;
  const creditsHeard = result?.discrepancies.find((d) => d.credits)?.heard_text;
  const manuscriptPane = result ? (credits && !find ? (
    // Credits aren't in the manuscript: no page of it to show, so what was heard.
    <div className="ms-state">
      <Icon name="info" size={22} />
      <div>
        <strong>{credits === "end" ? "End credits" : "Opening credits"}</strong>
        <p className="muted">
          The manuscript has no text for these, so they weren't compared to it. Listen to the
          file and check they're right.
        </p>
        {creditsHeard && <blockquote className="ms-credits-heard">{creditsHeard}</blockquote>}
      </div>
    </div>
  ) : (
    <ManuscriptPane
      manuscript={result.manuscript}
      discrepancies={highlightDiscs}
      categories={highlightCats}
      activeId={activeId}
      selectedId={selectedId}
      selectTick={selectTick}
      onSelect={selectFinding}
      onPlayAt={(t) => player.current?.playFrom(t)}
      onFlagSelection={flagFromSelection}
      pdfUrl={`/api/projects/${projectId}/manuscript.pdf`}
      pageOffset={result.page_offset}
      loadLayout={loadLayout}
      scriptMarks={scriptNotes?.state === "ready" ? scriptNotes.marks : undefined}
      projectId={projectId}
      chapterId={currentId}
      find={paneFind}
      onFindAll={startFind}
    />
  )) : null;

  // Find mode's list: the hit you're on opened up, with the flags on its words
  // as the report's own cards - to pick up, dismiss, re-label or edit there.
  const currentHit = find && hit ? (
    <CurrentHit
      h={hit}
      n={find.index + 1}
      state={stateOf(hit)}
      ready={!hit.chapter_id || hitHere}
      onPlay={() => playHit(hit)}
      action={findAction}
      flags={hitFlags.length ? hitFlags.map((f) => (
        <FindingRow
          key={f.key + f.d.id}
          f={f}
          selectedId={selectedId}
          activeId={activeId}
          autoEditId={autoEditId}
          onSelect={selectFinding}
          onDismiss={dismiss}
          onDismissAll={dismissAllIdentical}
          onPickup={pickup}
          onEditComment={editComment}
          onEditTitle={setTitle}
          onSetMeaning={setMeaning}
          onSetCategory={setCategory}
          projectId={projectId!}
          chapterId={currentId!}
          scriptMarks={scriptMarks}
          scriptHidden={scriptHidden}
          onHideScript={toggleScriptHidden}
          onIntended={intended}
        />
      )) : null}
    />
  ) : null;

  return (
    <div className="report-shell">
    <ProjectHeader projectId={projectId!} name={project.name} active="proofing" />
    <div className="report studio">
      <div className="report-head">
        <div className="row" style={{ gap: 10 }}>
          <select
            className="input"
            style={{ width: "auto", maxWidth: 280 }}
            value={currentId}
            onChange={(e) => navigate(`/projects/${projectId}/report/${e.target.value}`)}
          >
            {proofed.map((c) => (
              // Identify by the audio file (what the project page shows), not the
              // chapter name — that can be a stray manuscript heading (e.g. "2")
              // rather than the file, which is confusing in the switcher.
              <option key={c.id} value={c.id}>
                {c.audio_filename ? shownStem(c) : c.name}
              </option>
            ))}
          </select>
          <button className="btn ghost sm" disabled={idx <= 0} onClick={() => gotoChapter(-1)}>
            <Icon name="chevron-left" size={15} />
          </button>
          <button className="btn ghost sm" disabled={idx >= proofed.length - 1} onClick={() => gotoChapter(1)}>
            <Icon name="chevron-right" size={15} />
          </button>
        </div>
        {result && (
          // Grouped by what each control acts on, so it is clear which buttons are
          // about this file, which about the whole production, which lead to the
          // next step (the Pickups Package) and which are help for the tool itself.
          <div className="rv-tools">
            {project && <ProjectMenu project={project} />}
            <div className="rv-group" role="group" aria-label="This file">
              <span className="rv-group-label">This file</span>
              {thisAsset && (
                <VersionSwitch asset={thisAsset} viewing={currentId ?? null}
                  busy={switchVersion.isPending} onSwitch={(v) => switchVersion.mutate(v)} />
              )}
              <button
                className="btn ghost sm"
                disabled={undoDepth === 0}
                onClick={undoLast}
                title="Undo last action (⌘Z / Ctrl+Z)"
              >
                <Icon name="rotate-ccw" size={15} /> Undo
              </button>
              <button className="btn sm" onClick={() => addManualFlag(currentTime.current)}>
                ＋ Flag here
              </button>
            </div>
            <div className="rv-group" role="group" aria-label="Whole production">
              <span className="rv-group-label">Whole production</span>
              <button
                className={`btn ghost sm${notesOpen ? " on" : ""}`}
                onClick={() => { setNotesOpen((v) => !v); setBoundaryDrawer(null); }}
                aria-expanded={notesOpen}
                aria-label="General notes"
                title="Notes about the whole production (mastering, EQ, noise…), shown at the top of the Pickups Package"
              >
                <Icon name="notebook" size={15} /> <span className="gn-btn-label">General notes</span>
              </button>
              <button
                className="btn ghost sm"
                onClick={() => setImportingMarkers(true)}
                title="Add markers exported from Adobe Audition as flags on their audio files"
              >
                <Icon name="upload" size={15} /> Import markers
              </button>
              <button
                className="btn ghost sm"
                onClick={() => setNoIssuesOpen(true)}
                title="Add a “No issues” flag 3 seconds before the end of every proofed file with nothing picked up"
              >
                <Icon name="check" size={15} /> No issues
              </button>
            </div>
            <div className="rv-group" role="group" aria-label="Next step">
              <span className="rv-group-label">Next step</span>
              <button
                className="btn ghost sm"
                onClick={() => window.open(`/projects/${projectId}/pickups`, "_blank", "noopener")}
                title="Open the Pickups Package in a new tab"
              >
                <Icon name="mic" size={15} /> Pickups
              </button>
              <button
                className="btn ghost sm"
                onClick={() => window.open(`/projects/${projectId}/guide`, "_blank", "noopener")}
                title="Open the Re-recording guide — the pickups grouped into the passages to record again — in a new tab"
              >
                <Icon name="list" size={15} /> Re-recording guide
              </button>
            </div>
            <div className="rv-group" role="group" aria-label="Help">
              <div className="kbd-help-wrap">
                <button
                  className={`btn ghost sm${showKbdHelp ? " on" : ""}`}
                  onClick={() => setShowKbdHelp((v) => !v)}
                  aria-expanded={showKbdHelp}
                  aria-haspopup="dialog"
                  title="Review flags by keyboard — see the shortcuts (press ?)"
                >
                  <Icon name="keyboard" size={15} /> Shortcuts
                </button>
                {showKbdHelp && (
                  <>
                    <div className="menu-backdrop" onClick={() => setShowKbdHelp(false)} />
                    <div className="kbd-help" role="dialog" aria-label="Keyboard shortcuts">
                      <div className="kbd-help-head">Review by keyboard</div>
                      <dl>
                        <div><dt><kbd>↓</kbd><span className="kbd-or">or</span><kbd>J</kbd></dt><dd>Next flag</dd></div>
                        <div><dt><kbd>↑</kbd><span className="kbd-or">or</span><kbd>K</kbd></dt><dd>Previous flag</dd></div>
                        <div><dt><kbd>P</kbd></dt><dd>Pick up / un-pick</dd></div>
                        <div><dt><kbd>D</kbd></dt><dd>Dismiss / restore</dd></div>
                        <div><dt><kbd>A</kbd></dt><dd>Dismiss all identical</dd></div>
                        <div><dt><kbd>C</kbd></dt><dd>Copy comment</dd></div>
                        <div><dt><kbd>Enter</kbd></dt><dd>Replay this flag</dd></div>
                        <div className="kbd-help-sep"><dt><kbd>Space</kbd></dt><dd>Play / pause</dd></div>
                        <div><dt><kbd>←</kbd><kbd>→</kbd></dt><dd>Prev / next chapter</dd></div>
                        <div><dt><kbd>⌘</kbd><kbd>Z</kbd></dt><dd>Undo</dd></div>
                        <div><dt><kbd>?</kbd></dt><dd>Toggle this help</dd></div>
                      </dl>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {find ? (
        <FindBar
          query={find.query}
          hits={hits}
          index={find.index}
          loading={findRes.isLoading}
          truncated={!!findRes.data?.truncated}
          autoplay={findAutoplay}
          onAutoplay={(on) => { setFindAutoplay(on); writeFindAutoplay(on); }}
          onSearch={searchAgain}
          onPrev={() => goHit(find.index - 1)}
          onNext={() => goHit(find.index + 1)}
          onDone={() => setFind(null)}
        />
      ) : result && (
        <ReportStrip
          stats={result.stats}
          analyzed={result.analyzed}
          available={result.analysis_available}
          running={!!analyzeJobId}
          onRun={(force) => startAnalyze.mutate(force)}
          meaningCounts={meaningCounts}
          activeMeanings={activeMeanings}
          onToggleMeaning={toggleMeaning}
          hasBoundary={!!result.boundary}
          startConfirmed={!!result.boundary?.start.confirmed}
          endConfirmed={!!result.boundary?.end.confirmed}
          openBoundary={boundaryDrawer}
          onToggleBoundary={(which) => { setBoundaryDrawer((cur) => (cur === which ? null : which)); setNotesOpen(false); }}
        />
      )}

      {result && (result.stats?.wer ?? 0) >= 0.45 && (
        <div className="extraction-warning" role="alert">
          <Icon name="info" size={18} />
          <div>
            <strong>The manuscript text may not have extracted correctly.</strong> Only{" "}
            {Math.round(Math.max(0, 1 - (result.stats.wer || 0)) * 100)}% of words match the audio —
            far below a normal narration. This usually means the manuscript PDF has a corrupted
            embedded-font text layer (it looks fine on screen, but its text extracts as garbage), so
            the flags below are unreliable. Re-export the PDF or request a clean copy from the
            publisher; we can also re-process it with OCR.
          </div>
        </div>
      )}

      {find || (!isLoading && result) ? (
        // One body for both, its panes in the same places: entering or leaving
        // Find mode keeps the manuscript panel as it was (its view, its place).
        <div className={`report-body${find ? " find-on" : ""}`}>
          {find ? (
            <FindRail
              projectId={projectId!}
              query={find.query}
              hits={hits}
              index={find.index}
              fp={pdfPages?.fp}
              sizes={pdfPages?.sizes}
              stateOf={stateOf}
              onGo={(i) => goHit(i)}
            />
          ) : null}
          <div className="pane-left scroll">
            {manuscriptPane ?? <div className="page muted">Opening {hit?.file ?? "the file"}…</div>}
          </div>
          <div className="pane-right">
            {find ? (
              <FindPanel
                hits={hits}
                index={find.index}
                loading={findRes.isLoading}
                stateOf={stateOf}
                note={findNote}
                onNote={setFindNote}
                onGo={(i) => goHit(i)}
                current={currentHit}
              />
            ) : (
              <>
                {analyzeJobId && (
                  <RunPanel key={analyzeJobId} jobId={analyzeJobId} title="AI analysis" onDone={onAnalyzeDone} />
                )}
                {(reproofState !== "idle" || reproofActive) && (
                  <div className="reproof-hint" role="status">
                    <span className="reproof-dot" />
                    {reproofState === "pending"
                      ? "Boundary saved — re-proofing this chapter shortly. Keep reviewing; it runs in the background."
                      : "Re-proofing in the background — the flags refresh automatically when it's done."}
                  </div>
                )}
                <ScriptReproofBanner projectId={projectId!} busy={reproofState !== "idle" || reproofActive} />
                <FindingsList
                  items={visible}
                  counts={counts}
                  activeTypes={activeTypes}
                  onToggleType={toggleType}
                  search={search}
                  onSearch={setSearch}
                  showDismissed={showDismissed}
                  onToggleShowDismissed={() => setShowDismissed((v) => !v)}
                  showFiltered={showFiltered}
                  onToggleShowFiltered={() => setShowFiltered((v) => !v)}
                  filteredCount={filteredCount}
                  selectedId={selectedId}
                  selectTick={selectTick}
                  activeId={activeId}
                  onSelect={selectFinding}
                  onDismiss={dismiss}
                  onDismissAll={dismissAllIdentical}
                  onPickup={pickup}
                  onEditComment={editComment}
                  onEditTitle={setTitle}
                  onSetMeaning={setMeaning}
                  onSetCategory={setCategory}
                  autoEditId={autoEditId}
                  projectId={projectId!}
                  chapterId={currentId!}
                  scriptMarks={scriptMarks}
                  scriptHidden={scriptHidden}
                  onHideScript={toggleScriptHidden}
                  onIntended={intended}
                  markedCount={markedCount}
                  onlyMarked={onlyMarked}
                  onToggleOnlyMarked={() => setOnlyMarked((v) => !v)}
                />
              </>
            )}
          </div>

          {/* Audio start / end review — slides in from the right when its strip
              toggle is pressed; the card stacks vertically (manuscript on top). */}
          {!find && result?.boundary && (
            <div className={`boundary-drawer${boundaryDrawer ? " open" : ""}`}>
              {boundaryDrawer && currentId && (
                <>
                  <div className="bd-head">
                    <button className="btn ghost sm" onClick={() => setBoundaryDrawer(null)} title="Close">✕ Close</button>
                  </div>
                  <BoundaryCard
                    key={boundaryDrawer}
                    projectId={projectId!}
                    chapterId={currentId}
                    which={boundaryDrawer}
                    side={boundaryDrawer === "start" ? result.boundary.start : result.boundary.end}
                    pdfUrl={result.boundary.pdf_url}
                    offset={result.boundary.offset}
                    onConfirmed={() => setBoundaryDrawer(null)}
                    onSaved={() => { if (currentId) scheduleReproof(currentId); }}
                  />
                </>
              )}
            </div>
          )}

          <div className={`boundary-drawer notes-drawer${notesOpen ? " open" : ""}`} aria-hidden={!notesOpen}>
            {notesOpen && (
              <>
                <div className="bd-head">
                  <button className="btn ghost sm" onClick={() => setNotesOpen(false)} title="Close (Esc)">✕ Close</button>
                </div>
                <GeneralNotesPanel projectId={projectId!} chapterId={currentId} />
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="page muted">Loading report…</div>
      )}

      {result && (
        <AudioPlayer
          ref={player}
          src={result.audio_url}
          preroll={result.preroll_seconds}
          flags={visible.map((f) => ({
            key: f.key,
            time: f.d.start_time ?? null,
            category: f.category,
          }))}
          find={playerFind}
          onTime={(t) => { currentTime.current = t; onTime(t); }}
          onPrevChapter={() => gotoChapter(-1)}
          onNextChapter={() => gotoChapter(1)}
          canPrev={idx > 0}
          canNext={idx < proofed.length - 1}
        />
      )}
      {importingMarkers && (
        <ImportMarkersDialog
          projectId={projectId!}
          targets={[...project.chapters]
            .filter((c) => c.audio_filename)
            .sort((a, b) => a.order_index - b.order_index)
            .map((c) => ({ id: c.id, label: shownFile(c) }))}
          onClose={() => setImportingMarkers(false)}
        />
      )}
      {noIssuesOpen && <NoIssuesDialog projectId={projectId!} onClose={() => setNoIssuesOpen(false)} />}
    </div>
    </div>
  );
}
